// 配額 badge 模型（消費 GET /v3/me/quota，restful 02 §7.2；spec 19 §6.1）。
//
// badge 的資料源「只有」/v3/me/quota：GET /v3/devices 的回應 schema 沒有 quota_state
// 欄位（spec 19 缺口 G-7）—— 不得在 device list 上臆造該欄位，一律以本端點各維度
// status 回讀為準（19 §6.3 狀態機）。型別形狀鏡像 02 §7.2 列表信封 data[]（亦見
// account/SubscriptionPage 同源宣告）。

export type QuotaStatus = 'ok' | 'warning' | 'over' | 'unknown';

export interface QuotaDim {
  used?: number | null;
  limit?: number | null;
  remaining?: number | null;
  /** used/limit 分數（0..1）；limit=null（ADR-001 未拍板）時為 null（02 §7.2）。 */
  usage_ratio?: number | null;
  status?: string; // ok | warning | over | unknown
  as_of?: number;
  period_start?: number;
  period_end?: number;
}

export interface QuotaEntry {
  device_id?: string;
  plan?: string;
  subscription_status?: string;
  quota?: Record<string, QuotaDim>;
}

export type QuotaLevel = 'warning' | 'over';

export interface QuotaBadgeInfo {
  level: QuotaLevel;
  /** warning badge「用量 {pct}%」的顯示值；over（紅覆蓋條不顯百分比）為 null。 */
  usageRatio: number | null;
}

/**
 * 取該 device 各維度「最壞」status 推 tile badge（19 §6.1 判定表）：
 *   任一維度 `over`    → 紅覆蓋條「雲端錄影已降級」（over 優先於 warning）
 *   任一維度 `warning` → 黃 badge「用量 {usage_ratio}%」
 *   全為 `ok` / `unknown` → 無 badge（回 null；unknown=limit 未定，不得顯誤導值）
 * warning 取群中最高 usage_ratio 當顯示值（最貼近上限的維度）。
 */
export function quotaBadge(entry: QuotaEntry | null | undefined): QuotaBadgeInfo | null {
  const quota = entry?.quota;
  if (!quota) return null;
  const dims = Object.values(quota);
  if (dims.some((d) => d.status === 'over')) return { level: 'over', usageRatio: null };
  const warns = dims.filter((d) => d.status === 'warning');
  if (warns.length === 0) return null;
  const usageRatio = warns.reduce<number | null>((acc, d) => {
    const r = typeof d.usage_ratio === 'number' && Number.isFinite(d.usage_ratio) ? d.usage_ratio : null;
    if (r == null) return acc;
    return acc == null ? r : Math.max(acc, r);
  }, null);
  return { level: 'warning', usageRatio };
}

/** usage_ratio（0..1 分數）→ 顯示百分比整數（19 §6.1「用量 {usage_ratio}%」）。 */
export function quotaPercent(ratio: number | null | undefined): number | null {
  if (typeof ratio !== 'number' || !Number.isFinite(ratio)) return null;
  return Math.round(Math.min(1, Math.max(0, ratio)) * 100);
}

export const QUOTA_DIMENSION_LABEL_KEY: Record<string, string> = {
  storage_gb: 'account.dimStorage',
  relay_gb: 'account.dimRelay',
  share_count: 'account.dimShareCount',
};

export const QUOTA_DIMENSION_UNIT_KEY: Record<string, string> = {
  storage_gb: 'account.unitGb',
  relay_gb: 'account.unitGb',
  share_count: 'account.unitPersons',
};
